/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import { DocumentIconOutline, FolderIconSolid } from '@neo4j-ndl/react/icons';
import { TreeView } from '@neo4j-ndl/react/next';
import { useCallback, useState } from 'react';

type FileNode = {
  children?: FileNode[];
  id: string;
  title: string;
};

const files: FileNode[] = [
  {
    children: [
      {
        children: [
          { id: 'report', title: 'Report.pdf' },
          { id: 'presentation', title: 'Presentation.pptx' },
        ],
        id: 'work',
        title: 'Work',
      },
      { id: 'resume', title: 'Resume.pdf' },
    ],
    id: 'documents',
    title: 'Documents',
  },
  {
    children: [
      { id: 'photo', title: 'Photo.jpg' },
      { id: 'screenshot', title: 'Screenshot.png' },
    ],
    id: 'images',
    title: 'Images',
  },
];

const walk = (nodes: FileNode[], visit: (node: FileNode) => void) => {
  nodes.forEach((node) => {
    visit(node);
    walk(node.children ?? [], visit);
  });
};

const getDescendantIds = (node: FileNode): string[] =>
  (node.children ?? []).flatMap((child) => [
    child.id,
    ...getDescendantIds(child),
  ]);

const getLeafIds = (node: FileNode): string[] =>
  node.children === undefined ? [node.id] : node.children.flatMap(getLeafIds);

/** Folders follow their contents: selected only when every leaf below them is. */
const syncFolders = (nodes: FileNode[], selected: Set<string>) => {
  nodes.forEach((node) => {
    if (node.children === undefined) {
      return;
    }
    syncFolders(node.children, selected);
    if (getLeafIds(node).every((id) => selected.has(id))) {
      selected.add(node.id);
    } else {
      selected.delete(node.id);
    }
  });
};

const Component = () => {
  const [selectedIds, setSelectedIds] = useState<string[]>(['resume']);
  const selected = new Set(selectedIds);

  /**
   * The tree reports the whole selection at once, so cascading folders onto
   * their contents takes a single pass even when a Shift range flipped many
   * rows.
   */
  const handleSelectedIdsChange = useCallback((nextIds: string[]) => {
    setSelectedIds((previousIds) => {
      const next = new Set(nextIds);
      const previous = new Set(previousIds);
      walk(files, (node) => {
        const isSelected = next.has(node.id);
        if (
          node.children === undefined ||
          isSelected === previous.has(node.id)
        ) {
          return;
        }
        getDescendantIds(node).forEach((id) => {
          if (isSelected) {
            next.add(id);
          } else {
            next.delete(id);
          }
        });
      });
      syncFolders(files, next);
      return [...next];
    });
  }, []);

  const renderNode = (node: FileNode) => {
    const leafIds = getLeafIds(node);
    const selectedLeafCount = leafIds.filter((id) => selected.has(id)).length;
    return (
      <TreeView.Item
        key={node.id}
        id={node.id}
        title={node.title}
        defaultExpanded
        isIndeterminate={
          node.children !== undefined &&
          selectedLeafCount > 0 &&
          selectedLeafCount < leafIds.length
        }
        leadingVisual={
          node.children === undefined ? (
            <DocumentIconOutline />
          ) : (
            <FolderIconSolid />
          )
        }
      >
        {node.children?.map(renderNode)}
      </TreeView.Item>
    );
  };

  return (
    <TreeView
      ariaLabel="File explorer"
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectedIdsChange={handleSelectedIdsChange}
    >
      {files.map(renderNode)}
    </TreeView>
  );
};

export default Component;
